import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { useBatchChat } from '../hooks/useBatchChat'
import { ChatMessage, ChatRoom, ChatReader, ChatMember } from '../types'
import {
  MessageCircle,
  Send,
  ArrowLeft,
  Users,
  Bell,
  BellOff,
  MoreVertical,
  Pencil,
  Trash2,
  Reply,
  X,
  ChevronUp,
  Loader2,
  Search,
  Smile,
  ImagePlus,
} from 'lucide-react'
import Picker from '@emoji-mart/react'
import emojiData from '@emoji-mart/data'

// ─── Image URL detection ──────────────────────────────────────────────────────

function isImageMessage(content: string): boolean {
  try {
    const url = new URL(content.trim())
    return /\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i.test(url.pathname)
      || url.pathname.includes('/chat-images/')
  } catch {
    return false
  }
}

// ─── Avatar helpers ───────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-red-500', 'bg-indigo-500', 'bg-teal-500',
  'bg-cyan-500', 'bg-rose-500',
]

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = seed.charCodeAt(i) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

function Avatar({ name, src, size = 'md', firstName, lastName }: { name: string; src?: string | null; size?: 'sm' | 'md' | 'lg'; firstName?: string; lastName?: string }) {
  const sz = size === 'sm' ? 'w-8 h-8 text-xs' : size === 'lg' ? 'w-12 h-12 text-base' : 'w-10 h-10 text-sm'
  // If firstName/lastName are provided, use their first chars for precise initials
  const label = firstName !== undefined
    ? `${firstName.trim()[0] ?? ''}${lastName?.trim()[0] ?? ''}`.toUpperCase()
    : initials(name)
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={`${sz} rounded-full object-cover shrink-0`}
      />
    )
  }
  return (
    <div className={`${sz} ${avatarColor(name)} rounded-full flex items-center justify-center text-white font-bold shrink-0 select-none`}>
      {label}
    </div>
  )
}

// ─── Room List Item ───────────────────────────────────────────────────────────

function RoomListItem({ room, isActive, onlineCount, onClick }: { room: ChatRoom; isActive: boolean; onlineCount: number; onClick: () => void }) {
  const lastMsg = room.last_message
  const preview = lastMsg
    ? lastMsg.deleted_at
      ? 'Message deleted'
      : isImageMessage(lastMsg.content)
        ? '📷 Photo'
        : lastMsg.content.length > 45
          ? lastMsg.content.slice(0, 45) + '…'
          : lastMsg.content
    : 'No messages yet'

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3 py-2.5 rounded-xl transition-colors flex items-center gap-3 ${
        isActive ? 'bg-blue-50' : 'hover:bg-gray-100'
      }`}
    >
      <div className="relative shrink-0">
        <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center leading-none ${avatarColor(room.name)}`}>
          <span className="text-[10px] font-bold text-white">{room.program ?? '??'}</span>
          <span className="text-[9px] text-white/80 font-medium">{room.batch_year}</span>
        </div>
        {onlineCount > 0 && (
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`text-sm truncate ${room.unread_count > 0 && !isActive ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>
              {room.name}
            </span>
            {room.is_muted && <BellOff size={11} className="text-gray-400 shrink-0" />}
          </div>
          {lastMsg?.sent_at && (
            <span className={`text-[10px] shrink-0 ${room.unread_count > 0 && !isActive ? 'text-blue-500 font-semibold' : 'text-gray-400'}`}>
              {formatTimeShort(lastMsg.sent_at)}
            </span>
          )}
        </div>
        <div className="flex items-center justify-between gap-1 mt-0.5">
          <p className={`text-xs truncate ${room.unread_count > 0 && !isActive ? 'text-gray-900 font-bold' : 'text-gray-500'}`}>
            {lastMsg?.sender_name && !lastMsg.deleted_at ? `${lastMsg.sender_name.split(' ')[0]}: ` : ''}
            {preview}
          </p>
          {room.unread_count > 0 && !isActive && !room.is_muted && (
            <span className="min-w-[18px] h-[18px] flex items-center justify-center bg-blue-500 text-white text-[10px] font-bold rounded-full px-1 shrink-0">
              {room.unread_count > 99 ? '99+' : room.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

// ─── Message Bubble ───────────────────────────────────────────────────────────

function MessageBubble({
  message,
  isOwn,
  showAvatar,
  showSenderName,
  onReply,
  onEdit,
  onDelete,
}: {
  message: ChatMessage
  isOwn: boolean
  showAvatar: boolean
  showSenderName: boolean
  onReply: (msg: ChatMessage) => void
  onEdit: (msg: ChatMessage) => void
  onDelete: (msg: ChatMessage) => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const senderName = `${message.sender.first_name} ${message.sender.last_name}`

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setShowMenu(false)
    }
    if (showMenu) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  const isDeleted = !!message.deleted_at

  return (
    <div className={`flex items-end gap-2 mb-0.5 group ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar — others only, at last message of a consecutive group */}
      {!isOwn && (
        <div className="w-8 shrink-0 self-end mb-0.5">
          {showAvatar ? <Avatar name={senderName} src={message.sender.profile_picture} size="sm" firstName={message.sender.first_name} lastName={message.sender.last_name} /> : <div className="w-8" />}
        </div>
      )}

      <div className={`max-w-[70%] flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* Sender name — above first bubble in group, others only */}
        {!isOwn && showSenderName && (
          <p className="text-[11px] font-semibold text-gray-500 mb-0.5 ml-1">{senderName}</p>
        )}

        {/* Reply reference */}
        {message.reply_to_content && !isDeleted && (
          <div className={`text-[11px] px-3 py-1.5 mb-1 rounded-2xl border-l-2 max-w-full ${
            isOwn ? 'bg-blue-100 border-blue-300 text-blue-700' : 'bg-gray-100 border-gray-300 text-gray-600'
          }`}>
            <span className="font-semibold">{message.reply_to_sender}</span>
            <p className="truncate opacity-80">
              {isImageMessage(message.reply_to_content || '') ? '📷 Photo' : message.reply_to_content}
            </p>
          </div>
        )}

        <div className="relative flex items-center gap-1">
          {/* Context menu trigger */}
          {!isDeleted && (
            <div className={`relative ${isOwn ? 'order-first' : 'order-last'}`} ref={menuRef}>
              <button
                onClick={() => setShowMenu(!showMenu)}
                className="opacity-0 group-hover:opacity-100 active:opacity-100 touch-manipulation transition-opacity p-1.5 hover:bg-gray-200 active:bg-gray-200 rounded-full"
              >
                <MoreVertical size={14} className="text-gray-500" />
              </button>
              {showMenu && (
                <div className={`absolute z-20 ${isOwn ? 'right-0' : 'left-0'} bottom-7 bg-white shadow-xl rounded-xl border border-gray-100 py-1.5 min-w-[130px]`}>
                  <button
                    onClick={() => { onReply(message); setShowMenu(false) }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                  >
                    <Reply size={14} /> Reply
                  </button>
                  {isOwn && (
                    <>
                      <button
                        onClick={() => { onEdit(message); setShowMenu(false) }}
                        className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2 text-gray-700"
                      >
                        <Pencil size={14} /> Edit
                      </button>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        onClick={() => { onDelete(message); setShowMenu(false) }}
                        className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <Trash2 size={14} /> Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Bubble */}
          {(() => {
            const isImg = !isDeleted && isImageMessage(message.content)
            return (
              <div
                title={!isDeleted ? formatTooltip(message.sent_at, !!message.edited_at) : undefined}
                className={`w-fit rounded-2xl overflow-hidden ${
                  isDeleted
                    ? 'px-4 py-2 bg-gray-100 text-gray-400 italic border border-gray-200'
                    : isImg
                      ? `p-1 ${isOwn ? 'bg-blue-500' : 'bg-white border border-gray-200 shadow-sm'}`
                      : isOwn
                        ? 'px-4 py-2 bg-blue-500 text-white rounded-br-sm'
                        : 'px-4 py-2 bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm'
                }`}
                onClick={() => !isDeleted && setShowMenu(false)}
              >
                {isDeleted ? (
                  <p className="text-sm">This message was deleted</p>
                ) : isImg ? (
                  <a href={message.content} target="_blank" rel="noopener noreferrer" className="block">
                    <img
                      src={message.content}
                      alt="Shared image"
                      className="max-w-[220px] max-h-[220px] rounded-xl object-cover block"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{message.content}</p>
                )}
              </div>
            )
          })()}
        </div>

      </div>
    </div>
  )
}

// ─── Date separator ───────────────────────────────────────────────────────────

function DateSeparator({ iso }: { iso: string }) {
  const d = new Date(iso)
  const now = new Date()
  // Compare calendar dates only (strip time) so 11 PM → 1 AM = "Yesterday", not "Today"
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const diffDays = Math.round((nowDay.getTime() - dDay.getTime()) / 86400000)
  const label =
    diffDays === 0
      ? 'Today'
      : diffDays === 1
        ? 'Yesterday'
        : diffDays < 7
          ? d.toLocaleDateString([], { weekday: 'long' })
          : d.toLocaleDateString([], { month: 'long', day: 'numeric', year: diffDays > 365 ? 'numeric' : undefined })

  return (
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 h-px bg-gray-200" />
      <span className="text-[11px] text-gray-400 font-medium">{label}</span>
      <div className="flex-1 h-px bg-gray-200" />
    </div>
  )
}

// ─── Read Receipts ────────────────────────────────────────────────────────────

const MAX_SHOWN = 5

function ReadReceiptAvatars({ readers, align }: { readers: ChatReader[]; align: 'left' | 'right' }) {
  if (readers.length === 0) return null
  const shown = readers.slice(0, MAX_SHOWN)
  const extra = readers.length - MAX_SHOWN
  return (
    <div className={`flex items-center mt-0.5 gap-0 ${align === 'right' ? 'justify-end' : 'justify-start'} ${align === 'right' ? 'pr-1' : 'pl-9'}`}>
      {/* Overlap avatars right-to-left so the first reader is on top */}
      <div className={`flex ${align === 'right' ? 'flex-row-reverse' : 'flex-row'}`}>
        {shown.map((r, i) => {
          const name = `${r.first_name} ${r.last_name}`
          const title = `Seen by ${name}`
          return r.profile_picture ? (
            <img
              key={r.alumni_id}
              src={r.profile_picture}
              alt={name}
              title={title}
              className="w-4 h-4 rounded-full object-cover border border-white -mx-0.5 shrink-0"
              style={{ zIndex: i }}
            />
          ) : (
            <div
              key={r.alumni_id}
              title={title}
              className={`w-4 h-4 rounded-full border border-white -mx-0.5 shrink-0 flex items-center justify-center text-[7px] font-bold text-white ${avatarColor(name)}`}
              style={{ zIndex: i }}
            >
              {`${r.first_name?.trim()[0] ?? ''}${r.last_name?.trim()[0] ?? ''}`.toUpperCase()}
            </div>
          )
        })}
      </div>
      {extra > 0 && (
        <span className="text-[9px] text-gray-400 ml-1">+{extra}</span>
      )}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BatchChatPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const {
    rooms,
    activeRoom,
    activeRoomId,
    messages,
    roomReaders,
    onlineMembers,
    loadingRooms,
    loadingMessages,
    hasMoreMessages,
    sending,
    initChat,
    selectRoom,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMoreMessages,
    toggleMute,
    uploadAndSendImage,
    fetchRoomMembers,
  } = useBatchChat()

  const [input, setInput] = useState('')
  const [search, setSearch] = useState('')
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [editingMsg, setEditingMsg] = useState<ChatMessage | null>(null)
  const [mobileShowChat, setMobileShowChat] = useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [showMembers, setShowMembers] = useState(false)
  const [members, setMembers] = useState<ChatMember[]>([])
  const [loadingMembers, setLoadingMembers] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const prevMessageCount = useRef(0)

  useEffect(() => { initChat() }, [initChat])

  // Close emoji picker on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false)
      }
    }
    if (showEmojiPicker) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showEmojiPicker])

  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevMessageCount.current = messages.length
  }, [messages.length])

  const insertEmoji = (emoji: { native: string }) => {
    const textarea = inputRef.current
    const native = emoji.native
    if (textarea) {
      const start = textarea.selectionStart ?? input.length
      const end = textarea.selectionEnd ?? input.length
      const newVal = input.slice(0, start) + native + input.slice(end)
      setInput(newVal)
      requestAnimationFrame(() => {
        textarea.setSelectionRange(start + native.length, start + native.length)
        textarea.focus()
      })
    } else {
      setInput(prev => prev + native)
    }
    setShowEmojiPicker(false)
  }

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    setUploadingImage(true)
    try {
      await uploadAndSendImage(file)
    } finally {
      setUploadingImage(false)
    }
  }

  const handleSelectRoom = (roomId: string) => {
    selectRoom(roomId)
    setMobileShowChat(true)
    setReplyTo(null)
    setEditingMsg(null)
    setInput('')
    setShowMembers(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim()) return
    if (editingMsg) {
      await editMessage(editingMsg.id, input.trim())
      setEditingMsg(null)
    } else {
      await sendMessage(
        input.trim(),
        replyTo?.id,
        replyTo?.content ?? null,
        replyTo ? `${replyTo.sender.first_name} ${replyTo.sender.last_name}` : null
      )
      setReplyTo(null)
    }
    setInput('')
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Desktop: Enter sends, Shift+Enter for newline. Mobile: Enter always inserts newline.
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches
    if (e.key === 'Enter' && !e.shiftKey && !isCoarsePointer) { e.preventDefault(); handleSubmit(e) }
    if (e.key === 'Escape') { setReplyTo(null); setEditingMsg(null); setInput(''); setShowEmojiPicker(false) }
  }

  const handleEdit = (msg: ChatMessage) => {
    setEditingMsg(msg); setReplyTo(null); setInput(msg.content); inputRef.current?.focus()
  }
  const handleDelete = async (msg: ChatMessage) => { await deleteMessage(msg.id) }
  const handleReply = (msg: ChatMessage) => {
    setReplyTo(msg); setEditingMsg(null); setInput(''); inputRef.current?.focus()
  }
  const handleToggleMute = async () => { if (activeRoomId) await toggleMute(activeRoomId) }

  const handleScroll = () => {
    const c = messagesContainerRef.current
    if (c && c.scrollTop === 0 && hasMoreMessages && !loadingMessages) loadMoreMessages()
  }

  const filteredRooms = rooms.filter(r => r.program !== null && r.name.toLowerCase().includes(search.toLowerCase()))

  // Per-message grouping metadata
  const messagesMeta = messages.map((msg, i) => {
    const prev = messages[i - 1]
    const next = messages[i + 1]
    const showDate = !prev || new Date(msg.sent_at).toDateString() !== new Date(prev.sent_at).toDateString()
    const isLastInGroup = !next || next.sender_id !== msg.sender_id
    const isFirstInGroup = !prev || prev.sender_id !== msg.sender_id || showDate
    return { showDate, isLastInGroup, isFirstInGroup }
  })

  // For each message, compute which readers have this as their last-read message
  const readersByMessage = new Map<string, ChatReader[]>()
  for (const reader of roomReaders) {
    const readAt = new Date(reader.last_read_at).getTime()
    let lastSeenMsgId: string | null = null
    for (const msg of messages) {
      if (new Date(msg.sent_at).getTime() <= readAt) lastSeenMsgId = msg.id
    }
    if (lastSeenMsgId) {
      const existing = readersByMessage.get(lastSeenMsgId) || []
      readersByMessage.set(lastSeenMsgId, [...existing, reader])
    }
  }

  return (
    <div className="fixed inset-0 z-10 flex bg-white overflow-hidden md:relative md:inset-auto md:z-auto md:flex-1 md:min-h-0">

      {/* ── Left Panel: Room List ─────────────────────────────────────────── */}
      <div className={`w-full md:w-[320px] md:min-w-[280px] border-r border-gray-200 flex flex-col bg-white ${mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
        <div className="px-4 pt-5 pb-3">
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => navigate(-1)}
              className="md:hidden p-1.5 hover:bg-gray-100 rounded-full -ml-1"
              aria-label="Go back"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <h2 className="text-2xl font-bold text-gray-900">Chats</h2>
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search rooms…"
              className="w-full pl-9 pr-3 py-2 bg-gray-100 rounded-full text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-colors"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {loadingRooms ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 size={24} className="animate-spin text-blue-500" />
            </div>
          ) : filteredRooms.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 px-4 text-center">
              <MessageCircle size={32} className="mb-2 opacity-50" />
              <p className="text-sm font-medium">{search ? 'No rooms found' : 'No batch rooms yet'}</p>
            </div>
          ) : (
            filteredRooms.map(room => (
              <RoomListItem
                key={room.id}
                room={room}
                isActive={room.id === activeRoomId}
                onlineCount={room.id === activeRoomId ? onlineMembers.length : 0}
                onClick={() => handleSelectRoom(room.id)}
              />
            ))
          )}
        </div>
      </div>

      {/* ── Right Panel: Chat Area ────────────────────────────────────────── */}
      <div className={`flex-1 flex flex-col bg-gray-50 relative ${!mobileShowChat ? 'hidden md:flex' : 'flex'}`}>
        {!activeRoom ? (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 gap-3">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
              <MessageCircle size={36} className="text-blue-400" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-gray-600 text-lg">Select a chat</p>
              <p className="text-sm text-gray-400 mt-1">Choose a batch room from the sidebar</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMobileShowChat(false)}
                  className="md:hidden p-1.5 hover:bg-gray-100 rounded-full mr-1"
                >
                  <ArrowLeft size={20} className="text-gray-600" />
                </button>
                <div className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center leading-none shrink-0 ${avatarColor(activeRoom.name)}`}>
                  <span className="text-[10px] font-bold text-white">{activeRoom.program ?? '??'}</span>
                  <span className="text-[9px] text-white/80 font-medium">{activeRoom.batch_year}</span>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-gray-900">{activeRoom.name}</h3>
                    {activeRoom.is_muted && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500">
                        <BellOff size={9} /> Muted
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Users size={11} />
                    <span>{activeRoom.member_count} members</span>
                    {onlineMembers.length > 0 && (
                      <span className="flex items-center gap-1 ml-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                        <span className="text-green-600">{onlineMembers.length} online</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={async () => {
                    if (!activeRoom) return
                    setShowMembers(true)
                    setLoadingMembers(true)
                    const list = await fetchRoomMembers(activeRoom.id)
                    setMembers(list)
                    setLoadingMembers(false)
                  }}
                  title="View members"
                  className="p-2.5 rounded-full text-gray-400 hover:bg-gray-100 transition-colors"
                >
                  <Users size={18} />
                </button>
                <button
                  onClick={handleToggleMute}
                  title={activeRoom.is_muted ? 'Unmute room' : 'Mute room'}
                  className={`p-2.5 rounded-full transition-colors ${
                    activeRoom.is_muted
                      ? 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-500'
                      : 'text-gray-400 hover:bg-gray-100'
                  }`}
                >
                  {activeRoom.is_muted ? <BellOff size={18} /> : <Bell size={18} />}
                </button>
              </div>
            </div>

            {/* Members panel */}
            {showMembers && (
              <div className="absolute inset-y-0 right-0 w-72 bg-white border-l border-gray-200 z-20 flex flex-col shadow-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-800 text-sm">
                    Members · {activeRoom.member_count}
                  </h4>
                  <button
                    onClick={() => setShowMembers(false)}
                    className="p-1.5 hover:bg-gray-100 rounded-full text-gray-500"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {loadingMembers ? (
                    <div className="flex items-center justify-center h-24">
                      <Loader2 size={20} className="animate-spin text-gray-400" />
                    </div>
                  ) : (
                    <ul className="py-2">
                      {members.map(m => {
                        const isOnline = onlineMembers.includes(m.alumni_id)
                        const isMe = m.alumni_id === user?.id
                        const fullName = `${m.first_name} ${m.last_name}`
                        return (
                          <li key={m.alumni_id} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50">
                            <div className="relative flex-shrink-0">
                              <Avatar name={fullName} src={m.profile_picture} size="sm" firstName={m.first_name} lastName={m.last_name} />
                              {isOnline && (
                                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate">
                                {fullName}
                                {isMe && <span className="ml-1 text-xs text-gray-400 font-normal">(you)</span>}
                              </p>
                              {isOnline && (
                                <p className="text-xs text-green-600">Online</p>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              </div>
            )}

            {/* Messages Area */}
            <div
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto px-4 py-4"
            >
              {hasMoreMessages && (
                <div className="flex justify-center mb-4">
                  <button
                    onClick={loadMoreMessages}
                    disabled={loadingMessages}
                    className="flex items-center gap-1.5 text-xs text-blue-500 hover:text-blue-700 bg-white px-3 py-1.5 rounded-full shadow-sm border border-gray-200 hover:shadow transition-shadow"
                  >
                    {loadingMessages ? <Loader2 size={12} className="animate-spin" /> : <ChevronUp size={12} />}
                    Load older messages
                  </button>
                </div>
              )}

              {!loadingMessages && messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                  <div className={`w-16 h-16 rounded-2xl flex flex-col items-center justify-center leading-none ${avatarColor(activeRoom.name)}`}>
                    <span className="text-sm font-bold text-white">{activeRoom.program ?? '??'}</span>
                    <span className="text-xs text-white/80 font-medium">{activeRoom.batch_year}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-500 mt-2">{activeRoom.name}</p>
                  <p className="text-xs text-gray-400">{activeRoom.member_count} members · Say hello!</p>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const { showDate, isLastInGroup, isFirstInGroup } = messagesMeta[i]
                  return (
                    <React.Fragment key={msg.id}>
                      {showDate && <DateSeparator iso={msg.sent_at} />}
                      <div className={isFirstInGroup && i > 0 ? 'mt-2' : 'mt-px'}>
                        <MessageBubble
                          message={msg}
                          isOwn={msg.sender_id === user?.id}
                          showAvatar={isLastInGroup}
                          showSenderName={isFirstInGroup && msg.sender_id !== user?.id}
                          onReply={handleReply}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                        />
                        <ReadReceiptAvatars
                          readers={readersByMessage.get(msg.id) || []}
                          align={msg.sender_id === user?.id ? 'right' : 'left'}
                        />
                      </div>
                    </React.Fragment>
                  )
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply / Edit strip */}
            {(replyTo || editingMsg) && (
              <div className="px-4 py-2.5 bg-blue-50 border-t border-blue-100 flex items-center gap-3">
                <div className={`w-0.5 h-8 rounded-full ${editingMsg ? 'bg-amber-400' : 'bg-blue-400'}`} />
                <div className="flex-1 min-w-0">
                  {replyTo && (
                    <>
                      <p className="text-[11px] font-semibold text-blue-600">
                        Replying to {replyTo.sender.first_name}
                      </p>
                      <p className="text-xs text-blue-400 truncate">
                        {isImageMessage(replyTo.content) ? '📷 Photo' : replyTo.content}
                      </p>
                    </>
                  )}
                  {editingMsg && (
                    <p className="text-[11px] font-semibold text-amber-600">Editing message</p>
                  )}
                </div>
                <button
                  onClick={() => { setReplyTo(null); setEditingMsg(null); setInput('') }}
                  className="p-1.5 hover:bg-blue-100 rounded-full"
                >
                  <X size={14} className="text-blue-400" />
                </button>
              </div>
            )}

            {/* Input Bar */}
            <form onSubmit={handleSubmit} className="relative px-4 py-3 bg-white border-t border-gray-200">
              {/* Hidden image file input */}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleImageSelect}
              />

              {/* Emoji Picker */}
              {showEmojiPicker && (
                <div ref={emojiPickerRef} className="absolute bottom-full left-4 mb-1 z-50">
                  <Picker
                    data={emojiData}
                    onEmojiSelect={insertEmoji}
                    theme="light"
                    set="native"
                    previewPosition="none"
                    skinTonePosition="none"
                  />
                </div>
              )}

              <div className="flex items-end gap-2">
                {user && (
                  <Avatar
                    name={`${(user.profile as any)?.first_name || ''} ${(user.profile as any)?.last_name || ''}`}
                    src={(user.profile as any)?.profile_picture || null}
                    size="sm"
                  />
                )}
                <div className="flex-1 bg-gray-100 rounded-3xl pl-1 pr-4 py-1.5 transition-colors focus-within:bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-0">
                  <div className="flex items-end gap-0.5">
                    <button
                      type="button"
                      onPointerDown={e => e.preventDefault()}
                      onClick={() => setShowEmojiPicker(v => !v)}
                      className="p-1.5 hover:bg-gray-200 active:bg-gray-200 rounded-full shrink-0 transition-colors"
                    >
                      <Smile size={18} className={showEmojiPicker ? 'text-blue-500' : 'text-gray-500'} />
                    </button>
                    <button
                      type="button"
                      onPointerDown={e => e.preventDefault()}
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploadingImage}
                      className="p-1.5 hover:bg-gray-200 active:bg-gray-200 rounded-full shrink-0 transition-colors"
                    >
                      {uploadingImage
                        ? <Loader2 size={18} className="animate-spin text-blue-500" />
                        : <ImagePlus size={18} className="text-gray-500" />}
                    </button>
                    <textarea
                      ref={inputRef}
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={editingMsg ? 'Edit your message\u2026' : 'Aa'}
                      rows={1}
                      className="flex-1 resize-none bg-transparent border-0 text-base md:text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0 max-h-28 block leading-5 py-0.5"
                      style={{ minHeight: '20px' }}
                      enterKeyHint="send"
                      inputMode="text"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={(!input.trim() && !uploadingImage) || sending}
                  onPointerDown={e => e.preventDefault()}
                  className="w-9 h-9 flex items-center justify-center bg-blue-500 text-white rounded-full hover:bg-blue-600 disabled:bg-gray-200 disabled:cursor-not-allowed transition-colors shrink-0"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calendarDiffDays(iso: string): number {
  const d = new Date(iso)
  const now = new Date()
  const dDay = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((nowDay.getTime() - dDay.getTime()) / 86400000)
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatTimeShort(iso: string): string {
  const d = new Date(iso)
  const diffDays = calendarDiffDays(iso)
  if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' })
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

function formatTooltip(iso: string, edited: boolean): string {
  const d = new Date(iso)
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  const diffDays = calendarDiffDays(iso)
  let day = ''
  if (diffDays === 0) day = 'Today'
  else if (diffDays === 1) day = 'Yesterday'
  else if (diffDays < 7) day = d.toLocaleDateString([], { weekday: 'long' })
  else day = d.toLocaleDateString([], { month: 'long', day: 'numeric', year: diffDays > 365 ? 'numeric' : undefined })
  return `${time} · ${day}${edited ? ' · edited' : ''}`
}
